import { FC, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { clearBebyggelseobjektVersioner, getBebyggelseobjektVersioner } from 'store/bebyggelseobjektVersionerSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaSpinner } from 'raa-react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';
import { TextButton } from 'components/sharedElements/sharedElements';

import { HTTP_STATUS } from 'models/appModels/Request';

import { formatDate } from 'utilities/dateUtilities';

export interface RegistreringshistorikSectionProps {
  bebyggelseobjektId: string;
}

const RegistreringshistorikSection: FC<RegistreringshistorikSectionProps> = ({ bebyggelseobjektId }) => {
  const dispatch = useAppDispatch();

  const {
    requestStatus,
    data: bebyggelseobjektVersioner,
    error
  } = useAppSelector((state: RootState) => state.bebyggelseobjektVersioner);

  useEffect(() => {
    dispatch(clearBebyggelseobjektVersioner());
  }, [bebyggelseobjektId, dispatch]);

  useEffect(() => {
    if (error?.status === HTTP_STATUS.NOT_FOUND) {
      dispatch(clearBebyggelseobjektVersioner());
    }
  }, [dispatch, error]);

  const loadVersioner = useCallback(() => {
    dispatch(getBebyggelseobjektVersioner(bebyggelseobjektId));
  }, [bebyggelseobjektId, dispatch]);

  return (
    <DrawerSection>
      <>
        <h2 className="tw-heading-md tw-mb-4">Registreringshistorik</h2>
        {(requestStatus === 'pending' && (
          <div className="tw-flex tw-justify-center tw-py-8">
            <RaaSpinner size="3rem" />
          </div>
        )) ||
          (!bebyggelseobjektVersioner && !error && (
            <TextButton icon="raa-icon-arrow-down" onClick={loadVersioner}>
              Visa
            </TextButton>
          ))}
        {error?.status === HTTP_STATUS.NOT_FOUND && <p>Publicerade versioner saknas</p>}
        {bebyggelseobjektVersioner && (
          <table className="tw-flex tw-flex-col">
            <thead>
              <tr className="tw-flex tw-gap-2 tw-text-left tw-text-raa-gray-5">
                <th className="tw-basis-2/5 tw-font-normal">Aktivitet</th>
                <th className="tw-basis-2/5 tw-font-normal">Utförd av</th>
                <th className="tw-basis-1/5 tw-font-normal">Version</th>
              </tr>
            </thead>
            <tbody>
              {bebyggelseobjektVersioner.map((version, index) => (
                <tr key={version.version_id} className="tw-flex tw-gap-2">
                  <td className="tw-basis-2/5">
                    {
                      <Link to={`/aktivitet/${version.aktivitetref.aktivitet_id}`} className="raa-link">
                        <span aria-hidden="true" className={`raa-icon raa-icon-collection !tw-no-underline`} />{' '}
                        {version.aktivitetref.aktivitetsnamn}{' '}
                        <span className="tw-whitespace-nowrap tw-italic">
                          ({version.aktivitetref.aktivitetsnummer})
                        </span>
                      </Link>
                    }
                  </td>
                  <td className="tw-basis-2/5">{version.publicerad_av_organisation}</td>
                  <td className="tw-basis-1/5">
                    {index === 0 && formatDate(version.publiceringsdatum)}
                    {index > 0 && (
                      <Link
                        to={`/bebyggelseobjekt/${bebyggelseobjektId}/version/${version.version_id}`}
                        className="raa-link"
                      >
                        <span aria-hidden="true" className={`raa-icon raa-icon-externallink !tw-no-underline`} />{' '}
                        {formatDate(version.publiceringsdatum)}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    </DrawerSection>
  );
};

export default RegistreringshistorikSection;
